import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowLeftIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';

const ForgotPasswordPage: React.FC = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <EnvelopeIcon className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-poppins font-semibold text-dark-black mb-4">
              Email envoyé !
            </h1>
            <p className="text-gray-600 mb-6">
              Nous avons envoyé un lien de réinitialisation à <strong>{email}</strong>. 
              Vérifiez votre boîte de réception et vos spams.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center text-bright-orange hover:underline"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo CCA */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto bg-forest-green rounded-full flex items-center justify-center mb-4">
              <span className="text-white text-2xl font-bold">CCA</span>
            </div>
            <h1 className="text-2xl font-poppins font-semibold text-dark-black">
              Réinitialiser le mot de passe
            </h1>
            <p className="text-gray-600 text-sm mt-2">
              Entrez votre email pour recevoir un lien de réinitialisation
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="form-label">Adresse email</label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input pl-10"
                  placeholder="votre.email@cca.fr"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Nous vous enverrons un lien sécurisé pour réinitialiser votre mot de passe
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Envoi en cours...
                </div>
              ) : (
                <>
                  <EnvelopeIcon className="inline-block h-5 w-5 mr-2" />
                  Envoyer le lien
                </>
              )}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-bright-orange transition-colors inline-flex items-center"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Retour à la connexion
              </Link>
            </div>
          </form>

          {/* Help Section */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-dark-black mb-2 flex items-center">
              <QuestionMarkCircleIcon className="h-5 w-5 mr-2 text-blue-600" />
              Besoin d'aide ?
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Si vous ne recevez pas l'email, vérifiez vos spams ou contactez l'administrateur.
            </p>
            <button className="text-sm text-blue-600 hover:underline">
              Contacter le support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
